(() => {
  if (customElements.get('mn-nfc-story')) return;
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

  // A small GPU deformation of the supplied illustration: breathing, head movement,
  // and local lip movement. The original artwork remains the texture and fallback.
  class MemoryPortrait {
    constructor(canvas, image) { this.canvas = canvas; this.image = image; this.active = false; this.time = 0; this.started = 0; }
    async init() {
      try {
        await this.image.decode();
        if(this.destroyed)return;
        const gl = this.canvas.getContext('webgl', { alpha: false, antialias: false, powerPreference: 'low-power' });
        if (!gl) return;
        this.gl = gl;
        const shader = (type, source) => { const s = gl.createShader(type); gl.shaderSource(s, source); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw Error(gl.getShaderInfoLog(s)); return s; };
        const vertex = shader(gl.VERTEX_SHADER, `
          attribute vec2 aUV; varying vec2 vUV; uniform float uTime;
          float area(vec2 p, vec2 c, vec2 r) { vec2 d=(p-c)/r; return exp(-dot(d,d)*2.0); }
          void main(){
            vUV=aUV; vec2 p=aUV;
            float left=area(p,vec2(.36,.24),vec2(.21,.30));
            float right=area(p,vec2(.62,.27),vec2(.23,.30));
            p.x += left*sin(uTime*1.2)*.0024 + right*sin(uTime*1.05+1.7)*.0025;
            p.y += left*sin(uTime*1.65)*.0015 + right*sin(uTime*1.45+.8)*.002;
            gl_Position=vec4(p.x*2.0-1.0,1.0-p.y*2.0,0,1);
          }`);
        const fragment = shader(gl.FRAGMENT_SHADER, `
          precision mediump float; varying vec2 vUV; uniform sampler2D uImage; uniform float uTime; uniform float uTalk;
          void main(){
            vec2 uv=vUV; vec2 center=vec2(.607,.197);
            vec2 d=uv-center;
            float angle=-.39; float c=cos(angle),s=sin(angle);
            vec2 local=vec2(c*d.x+s*d.y,-s*d.x+c*d.y);
            float mask=exp(-pow(local.x/.026,2.0)*2.0-pow(local.y/.013,2.0)*2.0);
            float syllable=(.5+.5*sin(uTime*17.0))*(.65+.35*sin(uTime*8.3));
            local.y /= 1.0+mask*syllable*uTalk*1.35;
            uv=center+vec2(c*local.x-s*local.y,s*local.x+c*local.y);
            gl_FragColor=texture2D(uImage,uv);
          }`);
        const program=gl.createProgram(); gl.attachShader(program,vertex); gl.attachShader(program,fragment); gl.linkProgram(program);
        if (!gl.getProgramParameter(program,gl.LINK_STATUS)) throw Error('Portrait program failed');
        gl.useProgram(program); this.program=program;
        const points=[], n=38, m=48;
        for(let y=0;y<m;y++) for(let x=0;x<n;x++){const a=x/n,b=y/m,c=(x+1)/n,d=(y+1)/m;points.push(a,b,c,b,a,d,c,b,c,d,a,d);}
        const buffer=gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER,buffer); gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(points),gl.STATIC_DRAW);
        const pos=gl.getAttribLocation(program,'aUV'); gl.enableVertexAttribArray(pos); gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
        const texture=gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D,texture); gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,this.image);
        this.resources={texture,buffer,vertex,fragment}; this.count=points.length/2;
        this.uTime=gl.getUniformLocation(program,'uTime'); this.uTalk=gl.getUniformLocation(program,'uTalk');
        gl.viewport(0,0,this.canvas.width,this.canvas.height); this.draw(0); this.canvas.classList.add('is-ready');
        if(this.active) this.run();
      } catch (_) { /* The original supplied image stays visible when WebGL is unavailable. */ }
    }
    draw(t){ if(!this.gl)return; const g=this.gl;g.uniform1f(this.uTime,t);g.uniform1f(this.uTalk,this.talk?1:0);g.drawArrays(g.TRIANGLES,0,this.count); }
    run(){ if(this.raf || !this.gl || !this.active)return; this.last=performance.now(); const frame=(now)=>{this.raf=0;if(!this.active)return;const dt=now-this.last;if(dt>=32){this.time+=Math.min(dt,80)/1000;this.draw(this.time);this.last=now;}this.raf=requestAnimationFrame(frame);};this.raf=requestAnimationFrame(frame); }
    setActive(active,talk){this.talk=talk;this.active=active;if(active)this.run();else{cancelAnimationFrame(this.raf);this.raf=0;}}
    destroy(){this.destroyed=true;this.setActive(false,false);if(this.gl){const g=this.gl,r=this.resources;if(r){g.deleteTexture(r.texture);g.deleteBuffer(r.buffer);g.deleteShader(r.vertex);g.deleteShader(r.fragment);}if(this.program)g.deleteProgram(this.program);}}
  }

  class NFCStory extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.mounted = true;
      this.abort = new AbortController();
      this.elapsed = 0;
      this.duration = 24000;
      this.inView = false;
      this.ready = false;
      this.userPaused = false;
      this.manualMotion = false;
      this.q = selector => this.querySelector(selector);
      this.stage = this.q('.mn-nfc__stage');
      if (!this.stage) {
        this.mounted = false;
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), {once:true, signal:this.abort.signal});
        return;
      }
      this.camera = this.q('.mn-nfc__camera');
      this.cuffPhoto = this.q('.mn-nfc__cuff-photo');
      this.photoFrame = this.q('.mn-nfc__photo-frame');
      this.backdrop = this.q('.mn-nfc__memory-backdrop');
      this.phoneLayer = this.q('.mn-nfc__phone-layer');
      this.phone = this.q('.mn-nfc__phone');
      this.signal = this.q('.mn-nfc__signal');
      this.portraitElement = this.q('.mn-nfc__portrait');
      this.heading = this.q('.mn-nfc__heading');
      this.scanCopy = this.q('.mn-nfc__scan-copy');
      this.memoryCopy = this.q('.mn-nfc__memory-copy');
      this.copyPanel = this.q('.mn-nfc__copy-panel');
      this.memory = this.q('.mn-nfc__memory');
      this.messages = [...this.querySelectorAll('.mn-nfc__message')];
      this.screen = this.q('.mn-nfc__screen');
      this.homeBar = this.q('.mn-nfc__home-bar');
      this.playbackToggle = this.q('.mn-nfc__playback-toggle');
      this.playbackToggle.addEventListener('click', () => this.togglePlayback(), {signal:this.abort.signal});
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduced.addEventListener('change', () => {
        this.pause();
        this.manualMotion = false;
        if (this.reduced.matches) this.elapsed = this.duration;
        else if (this.elapsed >= this.duration) this.elapsed = 0;
        this.render();
        this.updatePlaybackControl();
        this.resume();
      }, {signal:this.abort.signal});
      this.portrait = new MemoryPortrait(this.q('canvas'), this.q('.mn-nfc__girls'));
      this.measure();
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.stage);
      this.resizeObserver.observe(this.copyPanel);
      this.resizeObserver.observe(this.messages[0]);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        if (this.inView) { this.loadImages(); this.resume(); }
        else this.pause();
      }, {threshold:[0,.15]});
      this.observer.observe(this.stage);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause(); else this.resume();
      }, {signal:this.abort.signal});
      this.classList.add('is-enhanced','is-paused');
      this.updatePlaybackControl();
    }
    async loadImages() {
      if (this.loading) return;
      this.loading = true;
      const images = [...this.querySelectorAll('img')];
      images.forEach(image => { image.loading = 'eager'; });
      await Promise.allSettled(images.map(image => image.decode()));
      if (!this.isConnected || !this.mounted) return;
      this.ready = true;
      this.portrait.init();
      if (!this.motionEnabled) this.elapsed = this.duration;
      this.render();
      this.resume();
    }
    measure() {
      const w = this.stage.clientWidth, mobile = w < 768;
      if (!w) return;
      const message = this.messages[0];
      const h = this.stage.clientHeight;
      if (!w || !h) return;
      // Reserve a separate reading area. Measuring it keeps long copy and font
      // loading from covering the photo, especially on narrow screens.
      const camW = mobile ? w*.92 : Math.min(w*.60,h*.84*1672/941);
      const copyBottom = 12+this.copyPanel.offsetHeight;
      const camH = mobile ? Math.max(1,h-copyBottom-24) : camW*941/1672;
      const camX = mobile ? w*.04 : w*.36+(w*.60-camW)/2;
      const camY = mobile ? copyBottom+12 : (h-camH)/2;
      const fit = Math.max(camW/1672,camH/941);
      const imageW = 1672*fit, imageH = 941*fit;
      const cuffX = imageW*.373, cuffY = imageH*.643;
      const pw = Math.min(mobile ? w*.34 : w*.16,230,h*.24,mobile?camH*.27:Infinity);
      // These bounds register the HTML screen with the photographed iPhone.
      const phoneRatio = 1081/543;
      const messageW = mobile ? camW*.94 : Math.min(camW*.36,360);
      message.style.width=messageW+'px';
      const messageH=message.offsetHeight;
      // Fit the phone into the remaining mobile viewport, retaining full-size
      // text. The forearm continues behind the bubble rather than adding height.
      const finalW=mobile?Math.min(camW*.42,Math.max(1,camH-messageH-40)/(phoneRatio*1.049)):Math.min(camW*.25,camH*.32,260)*1.1;
      const finalH=finalW*phoneRatio;
      const finalX=mobile?camX+camW*.5-finalW*.3849:camX+camW*.33-finalW*.5;
      // Let the desktop forearm continue past the photo's lower edge instead
      // of exposing the straight bottom edge of the transparent hand asset.
      const finalY=mobile?camY+camH-messageH-30-finalH:camY+camH-finalH*1.25;
      const messageX=mobile?camX+camW*.03:camX+camW-messageW-camW*.04;
      const messageY=mobile?camY+camH-messageH-12:camY+(camH-messageH)/2;
      // Match the cuff's position in the supplied close-up and its object-position.
      const scanX = camW*.474, scanY = camH*.486;
      this.layout = {w,h,mobile,camW,camH,camX,camY,imageW,imageH,cuffX,cuffY,
        startX:(camW-imageW)*.48,startY:(camH-imageH)*.5,
        scanX,scanY,targetX:camX+scanX,targetY:camY+scanY,pw,ph:pw*phoneRatio,
        finalW,finalH,finalX,finalY};
      Object.assign(this.photoFrame.style,{left:camX+'px',top:camY+'px',width:camW+'px',height:camH+'px'});
      Object.assign(this.backdrop.style,{left:camX+'px',top:camY+'px',width:camW+'px',height:camH+'px'});
      Object.assign(message.style,{left:messageX+'px',top:messageY+'px'});
      Object.assign(this.camera.style,{left:'0px',top:'0px',width:imageW+'px',height:imageH+'px'});
      Object.assign(this.phone.style,{left:'0px',top:'0px'});
      this.render();
    }
    render() {
      if (!this.layout) return;
      const t = this.elapsed/1000, l = this.layout;
      const mix = (a,b,x) => a+(b-a)*x;
      const ease = (a,b) => {const p=clamp((t-a)/(b-a));return p*p*(3-2*p);};
      // Clear the overlays before a brief dissolve between different shot sizes.
      // Keeping the overlap short avoids a ghosted figure over the cuff detail.
      const approach=ease(5.8,7.4),touch=ease(7.4,8),expand=ease(8.8,11.8);
      const detailReveal = ease(4.55,4.95);
      // Restore the still first photo underneath the last scene, then dissolve
      // into it without changing the frame or leaving an empty frame at the loop.
      const returning = this.motionEnabled && t>=23.2;
      const loopFade = returning?ease(23.2,24):0;
      this.camera.style.transform=`translate3d(${l.startX}px,${l.startY}px,0) scale(1)`;
      this.cuffPhoto.style.opacity=returning?0:detailReveal;
      this.camera.setAttribute('aria-hidden',detailReveal>.5&&!returning?'true':'false');
      this.cuffPhoto.setAttribute('aria-hidden',detailReveal<.5||t>10.8?'true':'false');
      this.photoFrame.style.opacity=returning?1:1-ease(9.4,10.8);
      this.backdrop.style.opacity=ease(8.8,10.8)*(1-loopFade);
      // Hide the marker throughout the cut, including a pause either side, so its
      // new position and size never read as a jump between the two photographs.
      const detailSignal = detailReveal>=.5&&!returning;
      Object.assign(this.signal.style,{
        left:(detailSignal?l.scanX:l.startX+l.cuffX)+'px',
        top:(detailSignal?l.scanY:l.startY+l.cuffY)+'px',
        transform:'translate(-50%,-50%)'
      });
      // Enlarge only the NFC symbol in the close-up; keep its label small and legible.
      this.signal.style.setProperty('--mn-nfc-signal-scale',detailSignal?(l.mobile?1.25:1.5):1);
      this.signal.style.opacity=returning?ease(23.5,24):(detailSignal?ease(5.1,5.55):1-ease(4.05,4.35))*(1-ease(8.2,8.8));
      this.heading.style.opacity=returning?ease(23.5,24):1-ease(3.85,4.2);
      this.scanCopy.style.opacity=ease(5.1,5.6)*(1-ease(8.4,9.2));
      this.memoryCopy.style.opacity=ease(9.4,10.4)*(this.motionEnabled?1-ease(23,23.2):1);
      this.scanCopy.setAttribute('aria-hidden',+this.scanCopy.style.opacity<.5?'true':'false');
      this.memoryCopy.setAttribute('aria-hidden',+this.memoryCopy.style.opacity<.5?'true':'false');
      const phoneX=mix(mix(l.w*.9,l.targetX-l.pw*.4,approach),l.targetX-l.pw*.5,touch);
      const phoneY=mix(mix(l.h*.75,l.targetY-28,approach),l.targetY-32,touch);
      const rotation=mix(mix(18,-8,approach),-3,touch)*(1-expand);
      // The scan can enter from outside the photo; the final hand is cropped
      // to the photo frame while the fabric and neighboring content stay clear.
      this.phoneLayer.style.clipPath=`inset(${l.camY*expand}px ${Math.max(0,l.w-l.camX-l.camW)*expand}px ${(Math.max(0,l.h-l.camY-l.camH)+(l.mobile?12:0))*expand}px ${l.camX*expand}px)`;
      Object.assign(this.phone.style,{
        transform:`translate3d(${mix(phoneX,l.finalX,expand)}px,${mix(phoneY,l.finalY,expand)}px,0) rotate(${rotation}deg)`,
        width:mix(l.pw,l.finalW,expand)+'px',height:mix(l.ph,l.finalH,expand)+'px',
        opacity:ease(5.8,6.25)*(1-loopFade)
      });
      // Keep the display completely black throughout the NFC approach and tap.
      this.memory.style.opacity=ease(9.2,10.2);
      this.homeBar.style.opacity=ease(9.2,10.2)*.8;
      this.messages.forEach(message => {
        const progress=ease(11.8,12.6)*(1-loopFade);
        message.style.opacity=progress;
        message.style.transform=`translateY(${16*(1-progress)}px)`;
        message.setAttribute('aria-hidden',progress<.5?'true':'false');
      });
      this.phone.setAttribute('aria-hidden',t<9.2||loopFade>.5?'true':'false');
      this.dataset.scene=t<4.95||returning?'keepsake':t<11.6?'tap':'memory';
      this.dataset.elapsed=this.elapsed.toFixed(0);
      this.portrait?.setActive(this.ready&&this.inView&&!document.hidden&&!this.userPaused&&this.motionEnabled&&t>9.2&&t<21,t>=12.3&&t<20.5);
    }
    get motionEnabled() { return !this.reduced.matches || this.manualMotion; }
    updatePlaybackControl() {
      const label=!this.motionEnabled?'Play animation':this.userPaused?'Resume animation':'Pause animation';
      this.playbackToggle.setAttribute('aria-label',label);
      this.playbackToggle.title=label;
    }
    togglePlayback() {
      if (!this.motionEnabled) {
        // Reduced-motion visitors see a still scene unless they choose to play.
        this.manualMotion=true;
        this.userPaused=false;
        this.elapsed=0;
        this.portrait.time=0;
      } else this.userPaused=!this.userPaused;
      if (this.userPaused) this.pause();
      else { this.loadImages(); this.render(); this.resume(); }
      this.updatePlaybackControl();
    }
    resume() {
      if (!this.ready || !this.inView || document.hidden || this.userPaused || !this.motionEnabled || this.raf) return;
      this.classList.remove('is-paused');
      this.lastFrame=performance.now();
      const frame=now=>{
        this.raf=0;
        if (!this.inView || document.hidden || !this.isConnected || this.userPaused || !this.motionEnabled) return;
        this.elapsed+=Math.min(now-this.lastFrame,100);
        if (this.elapsed>=this.duration) {
          this.elapsed%=this.duration;
          this.portrait.time=0;
        }
        this.lastFrame=now;
        this.render();
        this.raf=requestAnimationFrame(frame);
      };
      this.raf=requestAnimationFrame(frame);
    }
    pause() {
      cancelAnimationFrame(this.raf);this.raf=0;
      this.classList.add('is-paused');
      this.portrait?.setActive(false,false);
    }
    disconnectedCallback() {
      this.pause();this.abort?.abort();this.observer?.disconnect();this.resizeObserver?.disconnect();this.portrait?.destroy();this.mounted=false;this.loading=false;
    }
  }
  customElements.define('mn-nfc-story',NFCStory);
})();
